import React from 'react';
import { Subscription } from '@apollo/client/react/components';
import { mapFilter } from '@/se/components/GraphQLCRUD';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  statChip: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    height: 40,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      fontSize: theme.typography.body1.fontSize,
    },
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.75),
    fontSize: theme.typography.body1.fontSize,
  },
  statValue: {
    fontWeight: 400,
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
  },
}));

export interface StatItem {
  title: string;
  getter: (data: any) => string | number;
}

interface StatChipsProps {
  subscription: any;
  items: StatItem[];
  filter?: any;
}

/**
 * StatChips - renders stat chips that can be placed in the list header.
 * Used as HeaderLeft prop for List/CollectionListOnly components.
 */
const StatChips: React.FC<StatChipsProps> = ({ subscription, items, filter }) => {
  const classes = useStyles();

  return (
    <Subscription subscription={subscription} variables={{ filter: mapFilter(filter) }}>
      {({ loading, error: _error, data }: { loading: boolean; error?: any; data?: any }) => (
        <Box className={classes.statsRow}>
          {items.map(({ title, getter }, i) => {
            const value = loading ? '...' : getter(data);

            return (
              <Chip
                key={i}
                className={classes.statChip}
                label={
                  <>
                    <span className={classes.statLabel}>{title}:</span>
                    <span className={classes.statValue}>{value}</span>
                  </>
                }
                size="small"
                variant="outlined"
              />
            );
          })}
        </Box>
      )}
    </Subscription>
  );
};

StatChips.displayName = 'StatChips';

export default StatChips;
